import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    CreateKycPresetDocument,
    type CreateKycPresetMutation,
    type CreateKycPresetMutationVariables,
} from '../../../../api/mutations/createKYCPreset';
import Form from '../../../../components/fields/Form';
import InputField from '../../../../components/fields/InputField';
import SwitchField from '../../../../components/fields/SwitchField';
import useHandleError from '../../../../utilities/useHandleError';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type AddKYCPresetFormProps = {
    moduleId: string;
    onComplete: () => void;
};

type FormValues = {
    displayName: string;
    isActive: boolean;
};

const initialValues: FormValues = { displayName: '', isActive: false };

const validator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredBoolean('isActive')
);

const AddKYCPresetForm = ({ moduleId, onComplete }: AddKYCPresetFormProps) => {
    const { t } = useTranslation('localCustomerModuleDetails');
    const { yesNoSwitch } = useSystemSwitchData();

    const apolloClient = useApolloClient();
    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('localCustomerModuleDetails:kycPresetsDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<CreateKycPresetMutation, CreateKycPresetMutationVariables>({
                    mutation: CreateKycPresetDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('localCustomerModuleDetails:kycPresetsDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [onComplete, t, apolloClient, moduleId]
    );

    const validate = useValidator(validator);

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="addKYCPresets" name="addKYCPresets" onSubmitCapture={handleSubmit}>
                    <InputField
                        {...t('localCustomerModuleDetails:kycPresetsDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                    <SwitchField
                        {...t('localCustomerModuleDetails:kycPresetsDrawer.fields.active', { returnObjects: true })}
                        name="isActive"
                        {...yesNoSwitch}
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default AddKYCPresetForm;
